import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, fmtUSD } from "@/lib/api";

interface PayInfo { id: string; title: string; amount: number; status: string; customer_name: string }

export default function PayStatus() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const returned = params.get("return") === "1";
  const { t } = useLang();
  const [waited, setWaited] = useState(0);
  const confirming = returned && waited < 24;
  const { data: l, isLoading } = useQuery<PayInfo>({
    queryKey: ["pay", id], retry: false, queryFn: async () => (await api.get(`/pay/${id}`)).data,
    refetchInterval: (q) => (confirming && q.state.data?.status !== "paid" ? 2500 : false),
  });
  useEffect(() => {
    if (!confirming || l?.status === "paid") return;
    const tm = setTimeout(() => setWaited((w) => w + 1), 2500);
    return () => clearTimeout(tm);
  }, [confirming, l?.status, waited]);
  return (
    <div className="bg-[#F6F3EC] min-h-[70vh]" data-testid="pay-status-page">
      <div className="max-w-xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="eyebrow">Legendary Vacation Club</p>
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm text-center">
          {isLoading ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#00A8A0]" /> : !l ? <p data-testid="pay-invalid" className="text-sm text-slate-600">{t("bal.invalid")}</p> : l.status === "cancelled" ? <p data-testid="pay-cancelled" className="text-sm text-slate-600">{t("bal.invalid")}</p> : l.status === "paid" ? (
            <div data-testid="pay-paid">
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#00A8A0]" />
              <h1 className="mt-4 font-display text-3xl text-[#0B192C]">{t("co.paidTitle")}</h1>
              <p className="mt-2 text-sm text-slate-600">{t("co.ref")} {l.id.slice(0, 8).toUpperCase()} · {fmtUSD(l.amount)} USD</p>
              <Link to="/" className="mt-6 inline-flex rounded-full bg-[#0B192C] px-6 py-3 text-sm font-semibold text-white">{t("co.home")}</Link>
            </div>
          ) : (
            <div data-testid="pay-pending">
              {confirming && <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#00A8A0]" />}
              <h1 className="mt-4 font-display text-2xl text-[#0B192C]">{fmtUSD(l.amount)} USD</h1>
              <p className="mt-3 text-sm text-slate-600">{returned ? (confirming ? t("co.confirming") : t("co.confirmSlow")) : t("co.liveNote")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
